import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ScenarioSuiteProfile, ScenarioSuiteSummary } from '../../api/api';
import { PreparationBadge, ProfilePreparation } from './ProfilePreparation';

export interface IScenarioPickerProps {
    suite: ScenarioSuiteSummary;
    // The ticked scenario ids. Every selectable scenario ticked is the whole suite; the page turns that into a
    // run without --only.
    selected: string[];
    dryRun: boolean;
    // Whether the run stops at its first failure, so a whole suite can be run and investigated one failure at a time.
    stopOnFailure: boolean;
    disabled: boolean;
    onSelectedChange: (selected: string[]) => void;
    onDryRunChange: (dryRun: boolean) => void;
    onStopOnFailureChange: (stopOnFailure: boolean) => void;
    // The start button and whatever stands in its way, drawn at the right of the bar under the list.
    action: ReactNode;
    // Whether the environment is set up for the suite, drawn above the profiles.
    environment?: ReactNode;
    // The scenarios a run would not get to, each with the checks that stop it.
    blocked?: Map<string, string[]>;
}

// A scenario the suite has switched off is shown but cannot be picked: the runner skips it whatever the
// selection says, so a checkbox that silently does nothing would be worse than a greyed out one.
const NONE_BLOCKED = new Map<string, string[]>();

export const selectableIds = (suite?: ScenarioSuiteSummary) =>
    (suite?.scenarios ?? [])
        .filter((scenario) => !scenario.disabled)
        .map((scenario) => scenario.id as string);

/** A checkbox for a whole group, which also has to be able to say "some of them". */
const GroupCheckbox = ({
    checked,
    indeterminate,
    disabled,
    label,
    onChange,
}: {
    checked: boolean;
    indeterminate: boolean;
    disabled: boolean;
    label: string;
    onChange: () => void;
}) => {
    const ref = useRef<HTMLInputElement>(null);

    // Indeterminate exists only as a DOM property; there is no attribute React could render for it.
    useEffect(() => {
        if (ref.current) {
            ref.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);

    return (
        <input
            ref={ref}
            type="checkbox"
            className="arl-profile__check"
            checked={checked}
            disabled={disabled}
            aria-label={label}
            onChange={onChange}
        />
    );
};

interface IProfileGroupProps {
    profile: ScenarioSuiteProfile;
    scenarios: ScenarioSuiteSummary['scenarios'];
    knowsEnvironment: boolean;
    selected: string[];
    disabled: boolean;
    onSelectedChange: (selected: string[]) => void;
    blocked: Map<string, string[]>;
}

/**
 * One profile and the scenarios under it, folded to its header by default.
 *
 * Grouped by profile because that is how the runner executes: it applies a profile once and runs everything
 * under it, so a selection inside one profile is much faster than the same number of scenarios spread out. The
 * header carries the checkbox for the whole profile, so a fold does not stand in the way of choosing.
 */
const ProfileGroup = ({
    profile,
    scenarios = [],
    knowsEnvironment,
    selected,
    disabled,
    onSelectedChange,
    blocked,
}: IProfileGroupProps) => {
    const [open, setOpen] = useState(false);

    const ids = scenarios
        .filter((scenario) => !scenario.disabled)
        .map((scenario) => scenario.id as string);
    const ticked = ids.filter((id) => selected.includes(id));
    const stopped = scenarios.filter((scenario) =>
        blocked.has(scenario.id as string)
    ).length;
    const all = ids.length > 0 && ticked.length === ids.length;
    const bodyId = `profile-${profile.id}-body`;

    const toggleGroup = () =>
        onSelectedChange(
            all
                ? selected.filter((id) => !ids.includes(id))
                : [...selected.filter((id) => !ids.includes(id)), ...ids]
        );

    const toggle = (id: string) =>
        onSelectedChange(
            selected.includes(id)
                ? selected.filter((value) => value !== id)
                : [...selected, id]
        );

    return (
        <div className={`arl-profile${open ? ' arl-profile--open' : ''}`}>
            <div className="arl-profile__head">
                <GroupCheckbox
                    checked={all}
                    indeterminate={ticked.length > 0 && !all}
                    disabled={disabled || ids.length === 0}
                    label={`All scenarios of profile ${profile.id}`}
                    onChange={toggleGroup}
                />

                <button
                    type="button"
                    className="arl-profile__toggle"
                    aria-expanded={open}
                    aria-controls={bodyId}
                    onClick={() => setOpen(!open)}
                >
                    <FontAwesomeIcon
                        icon={open ? 'angle-down' : 'angle-right'}
                        className="arl-profile__chevron"
                    />
                    <span className="arl-profile__name">
                        <code className="arl-profile__id">{profile.id}</code>
                        {profile.description && (
                            <span className="arl-profile__description">
                                {profile.description}
                            </span>
                        )}
                    </span>
                </button>

                <div className="arl-profile__meta">
                    {/* Said on the folded header too, or a stopped scenario hides inside a closed profile. */}
                    {stopped > 0 && (
                        <span className="arl-profile__stopped">
                            <FontAwesomeIcon icon="ban" />
                            {stopped} would not run
                        </span>
                    )}
                    {/* Which environment the flags come from, so "Orbisk" never has to be taken on trust. */}
                    {profile.featuresFrom && (
                        <span
                            className={`arl-profile__source${
                                profile.featuresProblem
                                    ? ' arl-profile__source--problem'
                                    : ''
                            }`}
                            title={
                                profile.featuresProblem ??
                                `The feature flags are read from ${profile.featuresFrom} when the run starts; the suite's own flags go on top`
                            }
                        >
                            <FontAwesomeIcon
                                icon={
                                    profile.featuresProblem
                                        ? 'triangle-exclamation'
                                        : 'database'
                                }
                            />
                            features from {profile.featuresFrom}
                        </span>
                    )}
                    <PreparationBadge
                        profile={profile}
                        knowsEnvironment={knowsEnvironment}
                    />
                    <span className="arl-profile__count">
                        {ticked.length} of {ids.length}
                    </span>
                    {/*
                        One profile at a time is the only way to run an environment whose function apps cannot be
                        made to re-read their configuration: the run then checks the profile is already set instead
                        of setting it, and a second profile would need somebody to change the environment halfway.
                    */}
                    <button
                        type="button"
                        className="arl-link-btn"
                        disabled={disabled || ids.length === 0}
                        onClick={() => onSelectedChange(ids)}
                    >
                        Only this
                    </button>
                </div>
            </div>

            <div className="arl-profile__body" id={bodyId} hidden={!open}>
                <ul className="arl-profile__scenarios">
                    {scenarios.map((scenario) => (
                        <li key={scenario.id}>
                            <label
                                className={`arl-scenario${
                                    scenario.disabled
                                        ? ' arl-scenario--disabled'
                                        : ''
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={selected.includes(
                                        scenario.id as string
                                    )}
                                    disabled={disabled || scenario.disabled}
                                    onChange={() =>
                                        toggle(scenario.id as string)
                                    }
                                />
                                <span className="arl-scenario__text">
                                    <span className="arl-scenario__title">
                                        {scenario.description || scenario.id}
                                        {scenario.disabled && (
                                            <span className="arl-scenario__off">
                                                off in the suite
                                            </span>
                                        )}
                                        {(scenario.tags ?? []).includes(
                                            'known-failure'
                                        ) && (
                                            <span
                                                className="arl-scenario__expected"
                                                title="Tagged known-failure: meant to fail until an open decision is taken."
                                            >
                                                expected to fail
                                            </span>
                                        )}
                                    </span>
                                    {scenario.why && (
                                        <span className="arl-scenario__why">
                                            {scenario.why}
                                        </span>
                                    )}
                                    <span className="arl-scenario__id">
                                        {scenario.id}
                                    </span>
                                    {blocked.has(scenario.id as string) && (
                                        <span className="arl-scenario__blocked">
                                            <FontAwesomeIcon icon="ban" />A run
                                            would not get to this:{' '}
                                            {(
                                                blocked.get(
                                                    scenario.id as string
                                                ) ?? []
                                            ).join('; ')}
                                        </span>
                                    )}
                                </span>
                            </label>
                        </li>
                    ))}
                </ul>

                <ProfilePreparation
                    profile={profile}
                    knowsEnvironment={knowsEnvironment}
                />
            </div>
        </div>
    );
};

/** Everything that decides what a run of one suite will do, from the prerequisites down to the start button. */
export const ScenarioPicker = ({
    suite,
    selected,
    dryRun,
    stopOnFailure,
    disabled,
    onSelectedChange,
    onDryRunChange,
    onStopOnFailureChange,
    action,
    environment,
    blocked,
}: IScenarioPickerProps) => {
    const groups = useMemo(() => {
        const scenarios = suite.scenarios ?? [];

        return (suite.profiles ?? [])
            .map((profile) => ({
                profile,
                scenarios: scenarios.filter(
                    (scenario) => scenario.profile === profile.id
                ),
            }))
            .filter((group) => group.scenarios.length > 0);
    }, [suite]);

    const all = selectableIds(suite);
    const knowsEnvironment = !suite.configurationProblem;

    return (
        <div className="arl-picker">
            {(suite.description || suite.origin) && (
                <p className="arl-picker__lead">
                    {suite.description}
                    {suite.origin && (
                        <code className="arl-picker__origin">
                            {suite.origin}
                        </code>
                    )}
                </p>
            )}

            {suite.configurationProblem && (
                <div className="alert alert-warning" role="status">
                    {suite.configurationProblem}
                </div>
            )}

            {environment}

            {/*
                No quick selection by tag: the tags are a vocabulary of the suite author, and without a word on
                what each one means a button per tag is a guess. They still work on the command line with --only.
            */}
            <div className="arl-picker__toolbar">
                <span className="arl-picker__toolbar-label">
                    Profiles and scenarios
                </span>
                <button
                    type="button"
                    className="arl-link-btn"
                    disabled={disabled || selected.length === all.length}
                    onClick={() => onSelectedChange(all)}
                >
                    Select all
                </button>
                <button
                    type="button"
                    className="arl-link-btn"
                    disabled={disabled || selected.length === 0}
                    onClick={() => onSelectedChange([])}
                >
                    Clear
                </button>
            </div>

            <div className="arl-picker__profiles">
                {groups.map((group) => (
                    <ProfileGroup
                        key={group.profile.id}
                        profile={group.profile}
                        scenarios={group.scenarios}
                        knowsEnvironment={knowsEnvironment}
                        selected={selected}
                        disabled={disabled}
                        onSelectedChange={onSelectedChange}
                        blocked={blocked ?? NONE_BLOCKED}
                    />
                ))}
            </div>

            <div className="arl-picker__foot">
                <label className="arl-picker__dry">
                    <input
                        type="checkbox"
                        checked={dryRun}
                        disabled={disabled}
                        onChange={(event) =>
                            onDryRunChange(event.target.checked)
                        }
                    />
                    <span>
                        Dry run
                        <span className="arl-picker__hint">
                            {' '}
                            simulate everything, create nothing in Chargebee or
                            Exact
                        </span>
                    </span>
                </label>

                <label className="arl-picker__dry">
                    <input
                        type="checkbox"
                        checked={stopOnFailure}
                        disabled={disabled}
                        onChange={(event) =>
                            onStopOnFailureChange(event.target.checked)
                        }
                    />
                    <span>
                        Stop at the first failure
                        <span className="arl-picker__hint">
                            {' '}
                            skip the scenarios after it
                        </span>
                    </span>
                </label>

                <div className="arl-picker__action">
                    <span className="arl-picker__hint">
                        {selected.length} of {all.length} selected
                    </span>
                    {action}
                </div>
            </div>
        </div>
    );
};
